import {
  HttpClient,
  HttpClientError,
  HttpBody,
  HttpClientRequest,
  HttpClientResponse
} from 'effect/unstable/http'
import { Effect } from 'effect'
import type { CommandError } from './fetch-errors'
import type { SDKCommand } from './sdk.types'
import {
  createHttpStatusError,
  createInvalidJsonResponseError,
  mapHttpClientError,
  requestTraceFromHttpRequest,
  responseHeadersToRecord
} from './internal/http-effect'
import { transformSDKResponse } from './transform-middleware'

const REQUEST_ID_HEADER = 'x-request-id'
const FOLDER_ID_HEADER = 'x-folder-id'
const ORGANIZATION_ID_HEADER = 'x-organization-id'

type SDKRequestPayload = SDKCommand

type SDKRequestMetadata = {
  folderId?: unknown
  organizationId?: unknown
}

export type SDKRequestHeadersProvider = () => Effect.Effect<Record<string, string>, unknown, never>
export type SDKRequestIdProvider = Effect.Effect<string>

export type SDKHttpClient = {
  execute: (request: SDKCommand) => Effect.Effect<unknown, CommandError>
}

export function makeSDKHttpClient(
  httpClient: HttpClient.HttpClient,
  opts: {
    baseUrl: string
    getRequestHeaders?: SDKRequestHeadersProvider
    makeRequestId: SDKRequestIdProvider
  }
): SDKHttpClient {
  const getRequestHeaders = opts?.getRequestHeaders ?? (() => Effect.succeed({}))
  const makeRequestId = opts.makeRequestId

  const transportClient = httpClient.pipe(
    HttpClient.mapRequest(HttpClientRequest.prependUrl(opts.baseUrl)),
    HttpClient.mapRequestEffect((request) =>
      getRequestHeaders().pipe(
        Effect.map((headers) => HttpClientRequest.setHeaders(request, headers)),
        Effect.mapError(
          (cause) =>
            new HttpClientError.HttpClientError({
              reason: new HttpClientError.TransportError({
                request,
                cause,
                description: 'failed to resolve sdk request headers'
              })
            })
        )
      )
    ),
    HttpClient.transformResponse((effect) =>
      effect.pipe(Effect.provideService(HttpClient.TracerPropagationEnabled, false))
    ),
    HttpClient.catchTag('HttpClientError', (error) => Effect.fail(mapHttpClientError(error))),
    HttpClient.filterOrElse(
      (response) => response.status == 200,
      (response) => failForHttpStatus(response)
    )
  )

  const execute: SDKHttpClient['execute'] = (request) =>
    prepareSDKRequest(request, makeRequestId).pipe(
      Effect.mapError(mapHttpClientError),
      Effect.flatMap((httpRequest) => transportClient.execute(httpRequest)),
      Effect.flatMap(decodeSdkJsonResponse)
    )

  return { execute }
}

function prepareSDKRequest(
  payload: SDKRequestPayload,
  makeRequestId: SDKRequestIdProvider
): Effect.Effect<HttpClientRequest.HttpClientRequest, HttpClientError.HttpClientError> {
  const request = HttpClientRequest.post('/' + payload.type)

  return prepareSDKPayloadRequest(request, payload, makeRequestId)
}

function buildSDKHeaders(payload: SDKRequestPayload, requestId: string): Record<string, string> {
  const metadata = payload as SDKRequestMetadata

  const headers: Record<string, string> = {
    [REQUEST_ID_HEADER]: requestId,
    'content-type': 'application/json'
  }

  if (metadata.folderId) {
    headers[FOLDER_ID_HEADER] = String(metadata.folderId)
  }

  if (metadata.organizationId) {
    headers[ORGANIZATION_ID_HEADER] = String(metadata.organizationId)
  }

  return headers
}

function prepareSDKPayloadRequest(
  request: HttpClientRequest.HttpClientRequest,
  payload: SDKRequestPayload,
  makeRequestId: SDKRequestIdProvider
): Effect.Effect<HttpClientRequest.HttpClientRequest, HttpClientError.HttpClientError> {
  return setSDKPayloadHeaders(request, payload, makeRequestId).pipe(
    Effect.flatMap((next) => encodeSDKPayloadBody(next, payload))
  )
}

function setSDKPayloadHeaders(
  request: HttpClientRequest.HttpClientRequest,
  payload: SDKRequestPayload,
  makeRequestId: SDKRequestIdProvider
): Effect.Effect<HttpClientRequest.HttpClientRequest> {
  return makeRequestId.pipe(
    Effect.map((requestId) =>
      HttpClientRequest.setHeaders(request, buildSDKHeaders(payload, requestId))
    )
  )
}

function encodeSDKPayloadBody(
  request: HttpClientRequest.HttpClientRequest,
  payload: SDKRequestPayload
): Effect.Effect<HttpClientRequest.HttpClientRequest, HttpClientError.HttpClientError> {
  return Effect.try({
    try: () =>
      HttpClientRequest.setBody(
        request,
        HttpBody.raw(JSON.stringify(payload), { contentType: 'application/json' })
      ),
    catch: (cause) =>
      new HttpClientError.HttpClientError({
        reason: new HttpClientError.EncodeError({
          request,
          cause,
          description: 'failed to encode sdk request body'
        })
      })
  })
}

function failForHttpStatus(
  response: HttpClientResponse.HttpClientResponse
): Effect.Effect<never, CommandError> {
  const requestTrace = requestTraceFromHttpRequest(response.request)
  const responseHeaders = responseHeadersToRecord(response)

  return readSdkResponseBody(response).pipe(
    Effect.matchEffect({
      onSuccess: (body) =>
        Effect.fail(
          createHttpStatusError(requestTrace, {
            status: response.status,
            headers: responseHeaders,
            body
          })
        ),
      onFailure: (readError) =>
        Effect.fail(
          createHttpStatusError(
            requestTrace,
            {
              status: response.status,
              headers: responseHeaders
            },
            {
              cause: readError,
              description: 'failed to read non-200 response body'
            }
          )
        )
    })
  )
}

function readSdkResponseBody(response: HttpClientResponse.HttpClientResponse) {
  return response.headers['content-type']?.includes('application/json')
    ? response.json
    : response.text
}

function decodeSdkJsonResponse(
  response: HttpClientResponse.HttpClientResponse
): Effect.Effect<unknown, CommandError> {
  return response.json.pipe(
    Effect.map(transformSDKResponse),
    Effect.mapError(
      createInvalidJsonResponseError(requestTraceFromHttpRequest(response.request), response)
    )
  )
}
